import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  redirect,
  Scripts,
  ScrollRestoration,
} from "react-router";

import type { Route } from "./+types/root";
import { JsonLd } from "./components/seo/JsonLd";
import { organisationSchema } from "./lib/schema/organisation";
import GoogleAnalytics from "./components/utils/GoogleAnalytics";
import NotFoundPage from "./routes/not-found-page";
import { reportError } from "./utils/error-reporter";

export const links: Route.LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
  },
  {
    rel: "stylesheet",
    href: "https://unpkg.com/boxicons@2.1.4/css/boxicons.min.css"
  },
  {
    rel: "stylesheet",
    href: "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.css"
  }
];

// Plain ES5 only. This script must run on any Safari version we want to
// capture errors from, including ones too old to parse modern syntax.
// No arrow functions, no const/let, no template literals, no optional chaining.
const earlyErrorCatcher = `
(function () {
  try {
    var ENDPOINT = "/_actions/log-client-error";
    var queue = [];
    var flushing = false;

    function safeSend(payload) {
      try {
        var blob;
        if (typeof Blob !== "undefined") {
          blob = new Blob([payload], { type: "application/json" });
        }
        if (navigator.sendBeacon && blob) {
          if (navigator.sendBeacon(ENDPOINT, blob)) return;
        }
      } catch (e) {}
      try {
        if (typeof fetch === "function") {
          fetch(ENDPOINT, {
            method: "POST",
            body: payload,
            headers: { "Content-Type": "application/json" },
            keepalive: true,
            credentials: "same-origin"
          });
        }
      } catch (e) {}
    }

    function flush() {
      if (flushing || queue.length === 0) return;
      flushing = true;
      var batch = queue.slice(0);
      queue.length = 0;
      try {
        safeSend(JSON.stringify(batch));
      } catch (e) {}
      flushing = false;
    }

    function push(report) {
      try {
        report.url = window.location ? window.location.href : "";
        report.ua = navigator.userAgent || "";
        report.ts = Date.now();
        queue.push(report);
        // Debounce a touch so a burst becomes one POST.
        setTimeout(flush, 100);
      } catch (e) {}
    }

    window.addEventListener("error", function (ev) {
      var err = ev && ev.error;
      push({
        kind: "error",
        message: (ev && ev.message) ? String(ev.message) : (err && err.message ? String(err.message) : "unknown error"),
        source: ev && ev.filename ? String(ev.filename) : "",
        lineno: ev && typeof ev.lineno === "number" ? ev.lineno : null,
        colno: ev && typeof ev.colno === "number" ? ev.colno : null,
        stack: err && err.stack ? String(err.stack) : ""
      });
    }, true);

    window.addEventListener("unhandledrejection", function (ev) {
      var reason = ev && ev.reason;
      var msg = "unhandled rejection";
      var stack = "";
      if (reason) {
        if (typeof reason === "string") {
          msg = reason;
        } else if (reason.message) {
          msg = String(reason.message);
          if (reason.stack) stack = String(reason.stack);
        } else {
          try { msg = JSON.stringify(reason); } catch (e) {}
        }
      }
      push({ kind: "unhandledrejection", message: msg, stack: stack });
    });

    // Flush on page hide so reports aren't lost on navigation.
    window.addEventListener("pagehide", flush);
    window.addEventListener("visibilitychange", function () {
      if (document.visibilityState === "hidden") flush();
    });
  } catch (e) {
    // If the catcher itself fails, we are out of options.
  }
})();
`;

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <JsonLd data={organisationSchema} />
        <Links />
        {/* Must come BEFORE any other script so listeners exist before module evaluation. */}
        <script
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: earlyErrorCatcher }}
        />
      </head>
      <body>
        {children}
        <GoogleAnalytics />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  return (
    <Outlet />
  );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = "Oops!";
  let details = "An unexpected error occurred.";
  let stack: string | undefined;
  console.error("Error Boundary (Root): ", error);

  // Send the error through the same pipeline as the inline catcher.
  // Wrapped in a guard so SSR (no window) doesn't blow up.
  if (typeof window !== "undefined") {
    if (isRouteErrorResponse(error)) {
      reportError({
        kind: "errorboundary",
        message:
          "Route " + error.status + " " + (error.statusText || ""),
        extra: { data: typeof error.data === "string" ? error.data : null },
      });
    } else if (error instanceof Error) {
      reportError({
        kind: "errorboundary",
        message: error.message,
        stack: error.stack,
      });
    } else {
      reportError({
        kind: "errorboundary",
        message: typeof error === "string" ? error : "Unknown ErrorBoundary value",
      });
    }
  }

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Error";
    details =
      error.status === 404
        ? "The requested page could not be found."
        : error.statusText || details;
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  return (
    <main className="pt-16 p-4 container mx-auto">
      <h1>{message}</h1>
      <p>{details}</p>
      <p>Apologies for showing you this message. You should never see this. If you are seeing this, it means something went very wrong with our website. This message is only here so that the error is properly reported, and you don't just see a blank page.</p>
      <p>While we sort this out, please try calling us on 0333 188 7020, or emailing us at info@constellationtraining.co.uk.</p>
      {stack && (
        <pre className="w-full p-4 overflow-x-auto">
          <code>{stack}</code>
        </pre>
      )}
    </main>
  );
}